<script setup lang="ts">
import type { VariantProps } from 'tailwind-variants';
import type { HTMLAttributes } from 'vue';
import type { ThemeSlots } from '#layers/ui/types/tv';
import { tv } from 'tailwind-variants';
import { computed } from 'vue';
import { getBorderColor, getContrastRatio } from '#layers/ui/utils/colors';

interface ColorDotProps {
	backgroundColor?: string;
	color?: string;
	size?: ColorDotVariants['size'];
	ui?: ThemeSlots<typeof colorDot>;
	class?: HTMLAttributes['class'];
}

const props = withDefaults(defineProps<ColorDotProps>(), {
	backgroundColor: 'var(--ui-bg)',
});

const colorDot = tv({
	slots: {
		base: 'inline-flex items-center',
		dot: 'inline-block shrink-0 rounded-full',
	},
	variants: {
		size: {
			xs: { dot: 'size-2' },
			sm: { dot: 'size-3' },
			md: { dot: 'size-4' },
			lg: { dot: 'size-5' },
			xl: { dot: 'size-6' },
		},
		hasBorder: {
			true: { dot: 'ring-1 ring-inset' },
			false: {},
		},
	},
	defaultVariants: {
		size: 'md',
		hasBorder: false,
	},
});

type ColorDotVariants = VariantProps<typeof colorDot>;

const resolvedBackgroundColor = computed(() =>
	props.backgroundColor.startsWith('var(')
		? getComputedStyle(document.body).getPropertyValue(props.backgroundColor.slice(4, -1).trim())
		: props.backgroundColor,
);

const shouldShowBorder = computed(() => {
	if (!props.color) return false;

	try {
		const backgroundColor = resolvedBackgroundColor.value;
		const contrastRatio = getContrastRatio(props.color, backgroundColor);
		return contrastRatio < 3;
	}
	catch {
		return true;
	}
});

const borderColor = computed(() => {
	if (!props.color || !shouldShowBorder.value) return;

	return getBorderColor(props.color) ?? 'var(--ui-border)';
});

const theme = computed(() => colorDot({
	size: props.size,
	hasBorder: shouldShowBorder.value,
}));
</script>

<template>
	<div :class="theme.base({ class: props.ui?.base })" aria-hidden="true">
		<div
			class="bg-(--color-dot-color)"
			:class="[
				theme.dot({ class: props.ui?.dot }),
				props.class,
			]"
			:style="{ '--color-dot-color': props.color, '--tw-ring-color': borderColor }"
		/>
	</div>
</template>
